import { auth } from '../lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

const buildUrl = (path: string): string => {
  const base = API_BASE_URL || '';
  return path.startsWith('http') ? path : `${base}${path}`;
};

export const apiFetch = async <T = unknown>(path: string, init?: RequestInit): Promise<T> => {
  const headers = new Headers(init?.headers ?? {});

  await auth.authStateReady();
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Content-Type') && init?.body) {
    headers.set('Content-Type', 'application/json');
  }

  const url = buildUrl(path);
  if (!url) {
    throw { status: 500, message: 'URL da API inválida.' } as ApiError;
  }

  try {
    const response = await fetch(url, { ...init, headers, credentials: 'include' });

    if (!response.ok) {
      let message = response.statusText;
      const text = await response.text();
      try {
        const data = JSON.parse(text) as { message?: string; error?: string };
        message = data.message ?? data.error ?? message;
        throw { status: response.status, message, details: data } as ApiError;
      } catch (parseError) {
        if ((parseError as ApiError).status) throw parseError;
        throw { status: response.status, message: text || message } as ApiError;
      }
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof TypeError) {
      throw {
        status: 0,
        message: 'Erro de conexão. Verifique se o backend está rodando.',
        details: error,
      } as ApiError;
    }
    throw error;
  }
};

export default apiFetch;
